import { CoreButton, CoreCard, CoreSpinner } from "@verity/components/core";
import i18n from "../../../../i18n";
import { ContentProtectionState, FetchProgress } from ".";
import ScanAndCheckIcon from "../../../../images/portal/scan-and-check.svg?react";
import ErrorIcon from "../../../../images/portal/error.svg?react";
import WarningIcon from "../../../../images/portal/warning.svg?react";
import CompleteIcon from "../../../../images/portal/complete.svg?react";
import NoResultsImage from "../../../../images/portal/takedown-no-results.svg?react";
import styles from "./ScanSection.module.css";
import { Box, Stack, Typography } from "@mui/material";
import { Trans } from "react-i18next";
import UploadSection from "./UploadSection";
import { useEffect, useRef } from "react";
import { useMobileLayout } from "@verity/components";

type ScanSectionProps = {
	pageState: ContentProtectionState;
	onCancelScan: () => void;
	lastFetchedDate: string | null;
	expectedNumQuestions: number;
	onUserQuestionUploaded: (questions: string[]) => Promise<void>;
	onFetchAssessmentQuestions: () => Promise<void>;
	fetchProgress: FetchProgress;
	numQuestionsFound: number;
};

export default function ScanSection({
	pageState,
	onCancelScan,
	lastFetchedDate,
	expectedNumQuestions,
	numQuestionsFound,
	onUserQuestionUploaded,
	onFetchAssessmentQuestions,
	fetchProgress,
}: ScanSectionProps) {
	const scanButtonTextKey = pageState === "unstarted" ? "scan" : pageState === "scanInProgress" ? "cancel" : "rescan";
	const scanSectionRef = useRef<HTMLDivElement | null>(null);
	const isMobileLayout = useMobileLayout();

	// UI team wants focus to "jump up" when scanning is done
	useEffect(() => {
		if (pageState === "scanIncomplete" || pageState === "scanComplete") {
			setTimeout(() => {
				if (scanSectionRef.current) {
					scanSectionRef.current.focus({ preventScroll: true });
					scanSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
				}
			}, 50);
		}
	}, [pageState]);

	async function scanButtonHandler() {
		// Start scan
		if (pageState !== "scanInProgress") {
			await onFetchAssessmentQuestions();
		} else {
			onCancelScan();
		}
	}

	return (
		<CoreCard
			title={i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.title")}
			rightTitleContent={
				pageState === "scanComplete" || pageState === "scanIncomplete" ? (
					<Stack direction={"row"} spacing={0.5}>
						{pageState === "scanComplete" ? <CompleteIcon /> : <WarningIcon fill="#EE6700" />}
						<Typography className={styles.statusMessage} color={pageState === "scanComplete" ? "#3A8027" : "#CE4500"}>
							{i18n.t(`portal.components.contentProtectionPageTSX.scanAndCheck.${pageState}.status`, {
								total: numQuestionsFound,
							})}
						</Typography>
					</Stack>
				) : undefined
			}
			icon={<ScanAndCheckIcon fill="#42296E" />}
			useHeader
		>
			<Stack spacing={pageState === "scanComplete" && !isMobileLayout ? 0 : 1.5}>
				<Box className={styles.scanAndCheckCard} ref={scanSectionRef} tabIndex={-1}>
					<Typography className={styles.scanAndCheckDescription}>
						{i18n.t("portal.components.contentProtectionPageTSX.scanAndCheck.description")}
					</Typography>
					{pageState !== "scanIncomplete" && (
						<CoreButton
							className={styles.scanButton}
							color={pageState === "scanInProgress" ? "cancel" : "primary"}
							onClick={scanButtonHandler}
						>
							{i18n.t(`portal.components.contentProtectionPageTSX.scanAndCheck.scanButton.${scanButtonTextKey}`)}
						</CoreButton>
					)}
				</Box>
				{pageState !== "unstarted" && pageState !== "scanComplete" && (
					<Stack
						gap={1.5}
						sx={{
							backgroundColor: {
								scanFailed: "#FFFAFA",
								scanInProgress: "#FBFAFF",
								scanIncomplete: "#FFF9EF",
								noQuestions: "#f9f9f9",
							}[pageState],
						}}
						className={styles.scanAndCheckSubsection}
					>
						{pageState === "scanInProgress" && <CoreSpinner version="v2" />}
						{pageState !== "scanInProgress" &&
							(pageState === "scanFailed" ? (
								<ErrorIcon fill="#BF0033" />
							) : pageState === "scanIncomplete" ? (
								<WarningIcon fill="#EE6700" />
							) : (
								<NoResultsImage />
							))}
						<Typography className={styles.scanAndCheckSubsectionTitle}>
							{i18n.t(`portal.components.contentProtectionPageTSX.scanAndCheck.${pageState}.title`)}
						</Typography>
						<Typography className={styles.scanAndCheckSubsectionDesc}>
							<Trans
								i18nKey={`portal.components.contentProtectionPageTSX.scanAndCheck.${pageState}.description`}
								values={{
									numRetrieved: expectedNumQuestions - numQuestionsFound,
									totalQuestions: expectedNumQuestions,
								}}
								components={{ b: <strong /> }}
							/>
						</Typography>
						{pageState === "scanInProgress" && fetchProgress.total !== 0 && (
							<>
								<Typography className={styles.scanAndCheckSubsectionDesc}>
									<Trans
										i18nKey={"portal.components.contentProtectionPageTSX.scanAndCheck.scanInProgress.getSources"}
										values={{
											numScanned: fetchProgress.completed,
											total: fetchProgress.total,
										}}
										components={{ b: <strong /> }}
									/>
								</Typography>
								<Typography className={`${styles.scanAndCheckSubsectionDesc} ${styles.estimatedTime}`}>
									<Trans
										i18nKey={"portal.components.contentProtectionPageTSX.scanAndCheck.scanInProgress.estimatedTime"}
										values={{
											localizedTime: getEstimatedTimeRemaining(
												Math.max(fetchProgress.total - fetchProgress.completed, 0),
											),
										}}
										components={{ b: <strong /> }}
									/>
								</Typography>
							</>
						)}
						{pageState === "scanIncomplete" && (
							<CoreButton onClick={scanButtonHandler}>
								{i18n.t(`portal.components.contentProtectionPageTSX.scanAndCheck.scanButton.rescan`)}
							</CoreButton>
						)}
					</Stack>
				)}

				{pageState === "scanComplete" && lastFetchedDate && (
					<Typography className={styles.lastScanDate}>
						{i18n.t(`portal.components.contentProtectionPageTSX.scanAndCheck.scanComplete.lastScanDate`, {
							localizedDate: new Intl.DateTimeFormat(i18n.language, {
								month: "short",
								day: "numeric",
								year: "numeric",
							}).format(new Date(lastFetchedDate)),
						})}
					</Typography>
				)}
				{pageState === "scanIncomplete" && (
					<UploadSection
						onUpload={(questions: string[]) => onUserQuestionUploaded(questions)}
						numMissingQuestions={expectedNumQuestions - numQuestionsFound}
					/>
				)}
			</Stack>
		</CoreCard>
	);
}

function getEstimatedTimeRemaining(numQuestions: number): string {
	const totalSeconds = numQuestions * 8;

	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	const nf = (value: number, unit: "hour" | "minute" | "second") =>
		new Intl.NumberFormat(undefined, { style: "unit", unit, unitDisplay: "long" }).format(value);

	if (hours > 0) {
		return `${nf(hours, "hour")} ${nf(minutes, "minute")}`;
	}
	if (minutes > 0) {
		return `${nf(minutes, "minute")} ${nf(seconds, "second")}`;
	}
	return nf(seconds, "second");
}
